import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import { NavBar, SideNav } from "../../components";
import Breadcrumb from "../../components/Breadcrumb";
import {
  fetchAiConfig,
  grantEntitlement,
  saveAiConfig,
} from "../../redux/slices/aiConfig";

const emptyForm = {
  freeMsgsPerDay: 3,
  freeMatchesPerWeek: 1,
  premiumMsgFairUse: 100,
  advisorModel: "gpt-4o",
  advisorProvider: "openai",
  extractionModel: "gpt-4o-mini",
  featureEnabled: true,
  teaserFullCount: 1,
  fxRatesJson: "{}",
};

const AiConfig = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { config, pageLoading, actionLoading, error, entitlementResult } =
    useSelector((state) => state.aiConfig);
  const [form, setForm] = useState(emptyForm);
  const [fxError, setFxError] = useState(null);
  const [entitlement, setEntitlement] = useState({
    uid: "",
    tier: "premium",
    expiresAt: "",
    source: "comp",
  });

  const isAdmin = user && user.role === "admin";

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchAiConfig());
    }
  }, [dispatch, isAdmin]);

  useEffect(() => {
    if (!config) return;
    setForm({
      freeMsgsPerDay: config.freeMsgsPerDay ?? 3,
      freeMatchesPerWeek: config.freeMatchesPerWeek ?? 1,
      premiumMsgFairUse: config.premiumMsgFairUse ?? 100,
      advisorModel: config.advisorModel || "gpt-4o",
      advisorProvider: config.advisorProvider || "openai",
      extractionModel: config.extractionModel || "gpt-4o-mini",
      featureEnabled: config.featureEnabled !== false,
      teaserFullCount: config.teaserFullCount ?? 1,
      fxRatesJson: JSON.stringify(config.fx_rates_usd || {}, null, 2),
    });
  }, [config]);

  const handleSave = async (e) => {
    e.preventDefault();
    let fx_rates_usd;
    try {
      fx_rates_usd = JSON.parse(form.fxRatesJson || "{}");
      if (
        typeof fx_rates_usd !== "object" ||
        fx_rates_usd == null ||
        Array.isArray(fx_rates_usd)
      ) {
        throw new Error("fx_rates_usd must be a JSON object");
      }
      setFxError(null);
    } catch (err) {
      setFxError(err.message || "Invalid JSON for fx_rates_usd");
      return;
    }

    await dispatch(
      saveAiConfig({
        freeMsgsPerDay: Number(form.freeMsgsPerDay),
        freeMatchesPerWeek: Number(form.freeMatchesPerWeek),
        premiumMsgFairUse: Number(form.premiumMsgFairUse),
        advisorModel: form.advisorModel,
        advisorProvider: form.advisorProvider,
        extractionModel: form.extractionModel,
        featureEnabled: Boolean(form.featureEnabled),
        teaserFullCount: Number(form.teaserFullCount),
        fx_rates_usd,
      })
    );
  };

  const handleGrant = async (e) => {
    e.preventDefault();
    if (!entitlement.uid.trim()) return;
    await dispatch(
      grantEntitlement({
        uid: entitlement.uid.trim(),
        tier: entitlement.tier,
        expiresAt: entitlement.expiresAt.trim() || undefined,
        source: entitlement.source.trim() || "comp",
      })
    );
  };

  if (!isAdmin) {
    return (
      <div className="antialiased bg-gray-50 dark:bg-gray-900 min-h-screen">
        <NavBar />
        <SideNav />
        <main className="p-4 md:ml-64 h-auto pt-20">
          <Breadcrumb />
          <div className="mt-6 p-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
              AI Config
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              Admin access required to view or edit AI configuration.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="antialiased bg-gray-50 dark:bg-gray-900 min-h-screen">
      <NavBar />
      <SideNav />
      <main className="p-4 md:ml-64 h-auto pt-20">
        <div className="sticky top-14 z-10 bg-gray-50 dark:bg-gray-900">
          <Breadcrumb />
        </div>

        <section className="mb-4">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
            AI Config
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Global advisor quotas, models, feature flags, and FX rates.
          </p>
        </section>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 border border-red-100 text-sm">
            {String(error)}
          </div>
        )}

        {pageLoading && !config ? (
          <div className="py-16 text-center">
            <AiOutlineLoading className="inline h-6 w-6 animate-spin text-purple-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <form
              onSubmit={handleSave}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-4"
            >
              <h3 className="font-semibold text-gray-800 dark:text-white">
                Global config
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="text-sm text-gray-600">
                  Free msgs / day
                  <input
                    type="number"
                    className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                    value={form.freeMsgsPerDay}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        freeMsgsPerDay: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="text-sm text-gray-600">
                  Free matches / week
                  <input
                    type="number"
                    className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                    value={form.freeMatchesPerWeek}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        freeMatchesPerWeek: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="text-sm text-gray-600">
                  Premium msg fair use
                  <input
                    type="number"
                    className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                    value={form.premiumMsgFairUse}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        premiumMsgFairUse: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="text-sm text-gray-600">
                  Teaser full count
                  <input
                    type="number"
                    className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                    value={form.teaserFullCount}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        teaserFullCount: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="text-sm text-gray-600">
                  Advisor provider
                  <input
                    className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                    value={form.advisorProvider}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        advisorProvider: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="text-sm text-gray-600">
                  Advisor model
                  <input
                    className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                    value={form.advisorModel}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        advisorModel: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="text-sm text-gray-600 sm:col-span-2">
                  Extraction model
                  <input
                    className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                    value={form.extractionModel}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        extractionModel: e.target.value,
                      }))
                    }
                  />
                </label>
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  checked={form.featureEnabled}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      featureEnabled: e.target.checked,
                    }))
                  }
                />
                Feature enabled
              </label>

              <label className="block text-sm text-gray-600">
                FX rates USD (JSON)
                <textarea
                  rows={8}
                  className="mt-1 w-full rounded-lg border-gray-300 text-sm font-mono"
                  value={form.fxRatesJson}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, fxRatesJson: e.target.value }))
                  }
                />
              </label>
              {fxError && (
                <p className="text-sm text-red-600">{fxError}</p>
              )}

              <Button
                type="submit"
                color="purple"
                disabled={actionLoading}
                isProcessing={actionLoading}
                processingSpinner={
                  <AiOutlineLoading className="h-5 w-5 animate-spin" />
                }
              >
                Save config
              </Button>
            </form>

            <form
              onSubmit={handleGrant}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-4 h-fit"
            >
              <h3 className="font-semibold text-gray-800 dark:text-white">
                Grant entitlement
              </h3>
              <p className="text-sm text-gray-500">
                Sets{" "}
                <code className="text-xs">users/&#123;uid&#125;.entitlement</code>{" "}
                (source defaults to <code className="text-xs">comp</code>).
              </p>

              <label className="block text-sm text-gray-600">
                User uid
                <input
                  className="mt-1 w-full rounded-lg border-gray-300 text-sm font-mono"
                  value={entitlement.uid}
                  onChange={(e) =>
                    setEntitlement((p) => ({ ...p, uid: e.target.value }))
                  }
                  required
                />
              </label>

              <label className="block text-sm text-gray-600">
                Tier
                <select
                  className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                  value={entitlement.tier}
                  onChange={(e) =>
                    setEntitlement((p) => ({ ...p, tier: e.target.value }))
                  }
                >
                  <option value="free">free</option>
                  <option value="premium">premium</option>
                </select>
              </label>

              <label className="block text-sm text-gray-600">
                Expires at (ISO, optional)
                <input
                  className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                  placeholder="2027-01-01T00:00:00.000Z"
                  value={entitlement.expiresAt}
                  onChange={(e) =>
                    setEntitlement((p) => ({
                      ...p,
                      expiresAt: e.target.value,
                    }))
                  }
                />
              </label>

              <label className="block text-sm text-gray-600">
                Source
                <input
                  className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                  value={entitlement.source}
                  onChange={(e) =>
                    setEntitlement((p) => ({ ...p, source: e.target.value }))
                  }
                />
              </label>

              <Button
                type="submit"
                color="purple"
                disabled={actionLoading || !entitlement.uid.trim()}
                isProcessing={actionLoading}
                processingSpinner={
                  <AiOutlineLoading className="h-5 w-5 animate-spin" />
                }
              >
                Grant entitlement
              </Button>

              {entitlementResult && (
                <pre className="text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 overflow-x-auto">
                  {JSON.stringify(entitlementResult, null, 2)}
                </pre>
              )}
            </form>
          </div>
        )}
      </main>
    </div>
  );
};

export default AiConfig;
